import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { startAiImport, appendAiImport, getImportStatus, abortImport, commitImport, type ImportCandidate, type CommitPayload } from '@/utils/importUtils';

interface UseAiImportWizardParams {
  selectedPortfolioId?: string | null;
  onImported?: () => void;
  onClose?: () => void;
}

export function useAiImportWizard({ selectedPortfolioId, onImported, onClose }: UseAiImportWizardParams) {
  const [step, setStep] = useState<'intro' | 'processing' | 'review'>('intro');
  const [jobId, setJobId] = useState<string | null>(null);
  const [progressPct, setProgressPct] = useState<number>(10);
  const [statusMessage, setStatusMessage] = useState<string>('Scanning the file for assets...');
  const [items, setItems] = useState<ImportCandidate[]>([]);
  const [isCommitting, setIsCommitting] = useState(false);
  const [pendingAppends, setPendingAppends] = useState(0);
  const [totalAppends, setTotalAppends] = useState(0);
  const lastFilesRef = useRef<File[] | null>(null);
  const consecutiveErrorsRef = useRef(0);
  const fileNames = () => (lastFilesRef.current || []).map(f => f.name);

  const dedupeWithinImport = useCallback((rows: ImportCandidate[]): ImportCandidate[] => {
    try {
      const seen = new Set<string>();
      const norm = (s: string) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
      const res: ImportCandidate[] = [];
      for (const r of rows || []) {
        const currency = (r.currency || 'CAD').toUpperCase();
        const symbol = (r.symbol || '').trim().toUpperCase();
        const mvCents = Math.round(Number(r.market_value || 0) * 100);
        const nameKey = `NAME|${norm(r.asset_name)}|${currency}|${mvCents}`;
        const symKey = symbol ? `SYM|${symbol}|${currency}` : '';
        const key = symKey || nameKey;
        if (seen.has(key)) {
          res.push({ ...r, skip: true, notes: (r.notes ? r.notes + '; ' : '') + 'duplicate in current import' });
        } else {
          seen.add(key);
          res.push({ ...r });
        }
      }
      return res;
    } catch {
      return rows || [];
    }
  }, []);

  const onFilesSelected = useCallback(async (files: File[]) => {
    const list = Array.from(files || []).filter(Boolean);
    if (list.length === 0) return;
    lastFilesRef.current = list;
    try {
      const [first, ...rest] = list;
      setProgressPct(2);
      setStatusMessage(list.length > 1 ? `Uploading ${list.length} files…` : 'Uploading file…');
      setStep('processing');
      const start = await startAiImport(first);
      const id = start.jobId;
      setJobId(id);
      setProgressPct(5);
      setStatusMessage(list.length > 1 ? `Uploading and processing ${list.length} files...` : 'Uploading and preparing your file...');
      if (rest.length > 0) {
        setPendingAppends(rest.length);
        setTotalAppends(rest.length);
        (async () => {
          for (const f of rest) {
            try {
              await appendAiImport(id, f);
            } catch (err) {
              console.error('append error', err);
              toast.error(`One file failed to process: ${f.name}`);
            } finally {
              setPendingAppends((prev) => Math.max(0, prev - 1));
            }
          }
        })();
      }
    } catch (err: any) {
      console.error('ai-import-start error', err);
      toast.error('Unable to start AI import. Ensure edge functions are deployed.');
    }
  }, []);

  // Polling when processing
  useEffect(() => {
    if (step !== 'processing' || !jobId) return;
    let cancelled = false;
    const interval = setInterval(async () => {
      try {
        const s = await getImportStatus(jobId);
        if (cancelled) return;
        consecutiveErrorsRef.current = 0;
        setProgressPct(s.progressPct ?? 25);
        if (s.message) setStatusMessage(s.message);
        if (s.status === 'completed') {
          if (pendingAppends <= 0) {
            clearInterval(interval);
            const deduped = dedupeWithinImport(s.items || []);
            setItems(deduped);
            setStep('review');
          } else {
            const processed = Math.max(0, totalAppends - pendingAppends);
            setStatusMessage(`Processing additional files… (${processed}/${totalAppends})`);
          }
        } else if (s.status === 'aborted') {
          clearInterval(interval);
          const names = lastFilesRef.current?.map((f) => f.name) || [];
          const label = names.length > 1 ? `${names.length} files` : names[0] || undefined;
          toast.info(label ? `Import aborted for ${label}` : 'Import aborted');
          setStep('intro');
        } else if (s.status === 'failed') {
          clearInterval(interval);
          toast.error(s.message || 'Import failed');
          setStep('intro');
        }
      } catch (err) {
        console.error('ai-import-status error', err);
        setStatusMessage('Processing… (waiting for server)');
        const c = ++consecutiveErrorsRef.current;
        if (c === 3) toast.info('Network hiccup contacting import service… retrying');
        if (c >= 6) {
          clearInterval(interval);
          toast.error('Import service temporarily unreachable. Please try again in a moment.');
          setStep('intro');
        }
      }
    }, 1500);
    return () => {
      cancelled = true;
      clearInterval(interval);
      consecutiveErrorsRef.current = 0;
    };
  }, [step, jobId, pendingAppends, totalAppends, dedupeWithinImport]);

  const handleAbort = useCallback(async () => {
    const current = jobId;
    setStep('intro');
    setJobId(null);
    setItems([]);
    setPendingAppends(0);
    setTotalAppends(0);
    const names = lastFilesRef.current?.map((f) => f.name) || [];
    const label = names.length > 1 ? `${names.length} files` : names[0] || undefined;
    toast.info(label ? `Import aborted for ${label}` : 'Import aborted');
    consecutiveErrorsRef.current = 0;
    try {
      if (current) await abortImport(current);
    } catch (err) {
      console.error('ai-import-abort error (ignored)', err);
    }
  }, [jobId]);

  const handleRescan = useCallback(async () => {
    const files = lastFilesRef.current;
    if (!files || files.length === 0) {
      setStep('intro');
      return;
    }
    try {
      const [first, ...rest] = files;
      setProgressPct(2);
      setStatusMessage(files.length > 1 ? `Rescanning ${files.length} files…` : 'Rescanning…');
      setStep('processing');
      const start = await startAiImport(first);
      const newJobId = start.jobId;
      setJobId(newJobId);
      setProgressPct(5);
      if (rest.length > 0) {
        setPendingAppends(rest.length);
        setTotalAppends(rest.length);
        (async () => {
          for (const f of rest) {
            try {
              await appendAiImport(newJobId, f);
            } catch (err) {
              console.error('append error', err);
              toast.error(`One file failed to process: ${f.name}`);
            } finally {
              setPendingAppends((prev) => Math.max(0, prev - 1));
            }
          }
        })();
      }
    } catch (err) {
      toast.error('Rescan failed to start');
    }
  }, []);

  const handleEdit = useCallback((id: string | undefined, patch: Partial<ImportCandidate>) => {
    setItems((prev) =>
      prev.map((it, idx) => {
        const match = it.id ? it.id === id : false;
        if (match || (!it.id && !id && idx >= 0)) {
          return { ...it, ...patch } as ImportCandidate;
        }
        return it;
      })
    );
  }, []);

  const handleCommit = useCallback(async (overridePortfolioId?: string | null) => {
    if (!jobId) return;
    if (isCommitting) return;
    try {
      setIsCommitting(true);
      const selected = (items || []).filter((c) => !c.skip);
      const portfolioId = overridePortfolioId ?? selectedPortfolioId ?? undefined;
      const payload: CommitPayload = {
        items: selected.map((c, idx) => ({
          candidateId: c.id || String(idx),
          decision: c.duplicate_hint ? 'merge' : 'create',
          edits: {
            asset_name: c.asset_name,
            symbol: c.symbol,
            asset_type: c.asset_type,
            currency: (c.currency || '').toUpperCase(),
            quantity: c.quantity ?? undefined,
            market_value: Number(c.market_value || 0),
          },
          mergeTargetId: c.duplicate_hint?.matched_asset_id,
        })),
        ...(portfolioId ? { portfolio_id: portfolioId } : {}),
      };
      const result = await commitImport(jobId, payload);
      toast.success(`Imported ${result.created} • Merged ${result.merged} • Skipped ${result.skipped}`);
      try { onImported?.(); } catch {}
      onClose?.();
      setTimeout(() => {
        setStep('intro');
        setItems([]);
        setJobId(null);
      }, 300);
    } catch (err: any) {
      toast.error(err?.message || 'Import commit failed');
    } finally {
      setIsCommitting(false);
    }
  }, [jobId, isCommitting, items, selectedPortfolioId, onImported, onClose]);

  return {
    step,
    progressPct,
    statusMessage,
    items,
    setItems,
    isCommitting,
    onFilesSelected,
    handleAbort,
    handleRescan,
    handleEdit,
    handleCommit,
    setStep,
    jobId,
    fileNames,
  };
}
